// frontend/src/pages/admin/UserDetail.js
import { useState, useEffect } from "react";
import {
  ArrowLeft, Mail, Trash2, Shield, MapPin, Sliders,
  Pencil, X, AlertTriangle, CheckCircle, Building2,
} from "lucide-react";
import { adminUpdateUser, adminDeleteUser, adminResendInvite } from "../../library/allauth";

const ALL_FEATURES = [
  { key: "analytics", label: "Analytics" },
  { key: "alpr",      label: "ALPR"      },
  { key: "footage",   label: "Footage"   },
  { key: "map",       label: "Map"       },
];

// Role options shown in the edit dropdown
const ROLE_OPTIONS = [
  { value: "customer_user",  label: "Customer User"  },
  { value: "customer_admin", label: "Customer Admin" },
  { value: "xroadz_admin",   label: "Super Admin"    },
];

const ROLE_LABELS = {
  xroadz_admin:   "Super Admin",
  customer_admin: "Customer Admin",
  customer_user:  "Customer User",
  admin:          "Admin",      // legacy
  user:           "User",       // legacy
};

const STATUS_STYLES = {
  active:   { dot: "bg-green-500",  badge: "bg-green-100 text-green-700"   },
  pending:  { dot: "bg-yellow-400", badge: "bg-yellow-100 text-yellow-700" },
  disabled: { dot: "bg-gray-400",   badge: "bg-gray-100 text-gray-500"     },
};


// ── Confirm Save Modal ────────────────────────────────────────────────────────
function ConfirmSaveModal({ changes, onConfirm, onCancel, saving }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
            <AlertTriangle size={20} className="text-yellow-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Confirm Changes</h2>
            <p className="text-xs text-gray-400">Please review before saving</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            You are about to update:
          </p>
          <ul className="space-y-2">
            {changes.map((change, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>{change}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60">
            {saving ? "Saving..." : "Yes, Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Confirm Delete Modal ──────────────────────────────────────────────────────
function ConfirmDeleteModal({ username, onConfirm, onCancel, deleting }) {
  const [typed, setTyped] = useState("");
  const canDelete = typed === username;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <Trash2 size={20} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Delete User?</h2>
            <p className="text-xs text-gray-400">This action cannot be undone</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-5">
          <p className="text-sm text-red-700">
            You are about to permanently delete <strong>{username}</strong>.
            All their access and data will be removed.
          </p>
        </div>
        <div className="mb-5">
          <label className="text-xs text-gray-500 mb-1.5 block">
            Type <strong className="text-gray-700">{username}</strong> to confirm
          </label>
          <input
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={`Type "${username}" to confirm`}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              typed && !canDelete ? "border-red-300 focus:ring-red-400"
              : canDelete ? "border-green-300 focus:ring-green-400"
              : "border-gray-200 focus:ring-red-400"
            }`}
          />
          {typed && !canDelete && <p className="text-xs text-red-500 mt-1">Username doesn't match</p>}
          {canDelete && (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <CheckCircle size={12} /> Username confirmed
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} disabled={!canDelete || deleting} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {deleting ? "Deleting..." : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}


function FieldWarning({ message }) {
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <AlertTriangle size={12} className="text-yellow-500 flex-shrink-0" />
      <p className="text-xs text-yellow-600">{message}</p>
    </div>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────
export default function UserDetail({
  user,
  allRegions,
  onBack,
  onUpdated,
  onDeleted,
  currentUserRole,
  currentUsername,
}) {
  const [form, setForm]             = useState(null);
  const [original, setOriginal]     = useState(null);
  const [editMode, setEditMode]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [resending, setResending]   = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg]     = useState("");
  const [showSaveModal, setShowSaveModal]     = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isSuperAdmin = currentUserRole === "xroadz_admin";
  const isSelf = user.username === currentUsername;
  // console.log("isSelf check:", user.username, currentUsername, isSelf);

  useEffect(() => {
    if (user) {
      const initial = {
        first_name: user.first_name || "",
        last_name:  user.last_name  || "",
        role:       user.groups[0]  || "customer_user",
        is_active:  user.is_active,
        region_ids: user.regions.map(r => r.id),
        features:   user.features || { analytics: true, alpr: false, footage: true, map: true },
      };
      setForm(initial);
      setOriginal(initial);
      setEditMode(false);
      setSuccessMsg("");
      setErrorMsg("");
      setShowSaveModal(false);
      setShowDeleteModal(false);
    }
  }, [user]);

  if (!user || !form) return null;

  const getChanges = () => {
    const changes = [];
    if (form.first_name !== original.first_name)
      changes.push(`First Name: "${original.first_name || "empty"}" → "${form.first_name}"`);
    if (form.last_name !== original.last_name)
      changes.push(`Last Name: "${original.last_name || "empty"}" → "${form.last_name}"`);
    if (form.role !== original.role) {
      const oldLabel = ROLE_LABELS[original.role] || original.role;
      const newLabel = ROLE_LABELS[form.role]     || form.role;
      changes.push(`Role: ${oldLabel} → ${newLabel}`);
    }
    if (form.is_active !== original.is_active)
      changes.push(`Account: ${original.is_active ? "Active" : "Disabled"} → ${form.is_active ? "Active" : "Disabled"}`);
    ALL_FEATURES.forEach(f => {
      if (form.features[f.key] !== original.features[f.key])
        changes.push(`${f.label}: ${original.features[f.key] ? "ON" : "OFF"} → ${form.features[f.key] ? "ON" : "OFF"}`);
    });
    const addedRegions   = form.region_ids.filter(id => !original.region_ids.includes(id));
    const removedRegions = original.region_ids.filter(id => !form.region_ids.includes(id));
    addedRegions.forEach(id => {
      const r = allRegions.find(r => r.id === id);
      if (r) changes.push(`Region added: ${r.name}`);
    });
    removedRegions.forEach(id => {
      const r = allRegions.find(r => r.id === id);
      if (r) changes.push(`Region removed: ${r.name}`);
    });
    return changes;
  };

  const handleSaveClick = () => {
    const changes = getChanges();
    if (changes.length === 0) { setEditMode(false); return; }
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateUser(user.id, {
        first_name: form.first_name,
        last_name:  form.last_name,
        role:       form.role,
        is_active:  form.is_active,
        features:   form.features,
        region_ids: form.region_ids,
      });
      if (res.status === 200) {
        setSuccessMsg("Changes saved successfully.");
        setShowSaveModal(false);
        setEditMode(false);
        onUpdated();
      } else {
        setErrorMsg("Failed to save changes.");
        setShowSaveModal(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await adminDeleteUser(user.id);
      if (res.status === 200) { onDeleted(); onBack(); }
      else setErrorMsg("Failed to delete user.");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancelEdit = () => {
    setForm({ ...original });
    setEditMode(false);
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleResendInvite = async () => {
    setResending(true);
    try {
      await adminResendInvite(user.id);
      setSuccessMsg("Invite email sent.");
    } finally {
      setResending(false);
    }
  };

  const toggleRegion = id => {
    if (!editMode) return;
    setForm(f => ({
      ...f,
      region_ids: f.region_ids.includes(id)
        ? f.region_ids.filter(r => r !== id)
        : [...f.region_ids, id],
    }));
  };

  const toggleFeature = key => {
    if (!editMode) return;
    setForm(f => ({ ...f, features: { ...f.features, [key]: !f.features[key] } }));
  };

  const statusStyle = STATUS_STYLES[user.status] || STATUS_STYLES.active;
  const displayName = user.first_name || user.last_name
    ? `${user.first_name} ${user.last_name}`.trim()
    : user.username;
  const roleKey   = form.role || "customer_user";
  const roleLabel = ROLE_LABELS[roleKey] || roleKey;

  // Roles the current admin can assign
  const assignableRoles = ROLE_OPTIONS.filter(r =>
    // Customer Admin cannot assign xroadz_admin
    isSuperAdmin || r.value !== "xroadz_admin"
  );

  return (
    <div>
      {showSaveModal && (
        <ConfirmSaveModal
          changes={getChanges()}
          onConfirm={handleConfirmSave}
          onCancel={() => setShowSaveModal(false)}
          saving={saving}
        />
      )}
      {showDeleteModal && (
        <ConfirmDeleteModal
          username={user.username}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          deleting={deleting}
        />
      )}

      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-6">
        <ArrowLeft size={16} /> Back to Users
      </button>

      {/* User Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xl font-bold">
            {displayName[0].toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{displayName}</h1>
            <p className="text-sm text-gray-400">{user.email || "No email set"}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                {ROLE_LABELS[user.groups[0]] || user.groups[0] || "customer_user"}
              </span>
              {user.customer_name && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Building2 size={12} /> {user.customer_name}
                </span>
              )}
              <span className="text-xs text-gray-400">
                Joined {new Date(user.date_joined).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          {!editMode ? (
            !isSelf ? (
              <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors">
                <Pencil size={15} /> Edit User
              </button>
            ) : (
              <span className="px-4 py-2 text-sm text-gray-400 border border-gray-200 rounded-lg">
                Your account
              </span>
            )
          ) : (
            <>
              <button onClick={handleCancelEdit} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <X size={15} /> Cancel
              </button>
              <button onClick={handleSaveClick} className="px-5 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors">
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="mb-4 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
          <CheckCircle size={15} /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <AlertTriangle size={15} /> {errorMsg}
        </div>
      )}
      {editMode && (
        <div className="mb-4 px-4 py-2.5 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700 flex items-center gap-2">
          <AlertTriangle size={15} className="flex-shrink-0" />
          You are in edit mode. All changes will be reviewed before saving.
        </div>
      )}

      {/* 3 Cards */}
      <div className="grid grid-cols-3 gap-6 mb-6">

        {/* Card 1 — Account Info */}
        <div className={`bg-white rounded-xl border p-6 transition-colors ${editMode ? "border-yellow-200" : "border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-5">
            <Shield size={15} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Info</p>
          </div>
          <div className="space-y-4">

            {/* First Name */}
            <div>
              <p className="text-xs text-gray-400 mb-1">First Name</p>
              {editMode ? (
                <>
                  <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                  {form.first_name !== original.first_name && <FieldWarning message="You are changing the first name." />}
                </>
              ) : (
                <p className="text-sm font-medium text-gray-800">{form.first_name || "—"}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <p className="text-xs text-gray-400 mb-1">Last Name</p>
              {editMode ? (
                <>
                  <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                  {form.last_name !== original.last_name && <FieldWarning message="You are changing the last name." />}
                </>
              ) : (
                <p className="text-sm font-medium text-gray-800">{form.last_name || "—"}</p>
              )}
            </div>

            {/* Email — always read-only */}
            <div>
              <p className="text-xs text-gray-400 mb-1">Email</p>
              <p className="text-sm font-medium text-gray-800">{user.email || "—"}</p>
            </div>

            {/* Customer — always read-only */}
            {user.customer_name && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Customer</p>
                <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                  <Building2 size={13} className="text-gray-400" /> {user.customer_name}
                </p>
              </div>
            )}

            {/* Role */}
            <div>
              <p className="text-xs text-gray-400 mb-1">Role</p>
              {editMode ? (
                <>
                  <select
                    value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
                  >
                    {assignableRoles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  {form.role !== original.role && (
                    <FieldWarning message={`Role changing from ${ROLE_LABELS[original.role] || original.role} to ${ROLE_LABELS[form.role] || form.role}. This affects portal access.`} />
                  )}
                </>
              ) : (
                <p className="text-sm font-medium text-gray-800">{roleLabel}</p>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between pt-1">
              <div>
                <p className="text-sm font-medium text-gray-700">Account Active</p>
                <p className="text-xs text-gray-400">{form.is_active ? "Access enabled" : "Access blocked"}</p>
              </div>
              <button
                onClick={() => editMode && setForm({ ...form, is_active: !form.is_active })}
                disabled={!editMode}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? "bg-emerald-600" : "bg-gray-300"} ${!editMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
            {editMode && form.is_active !== original.is_active && (
              <FieldWarning message={`Account will be ${form.is_active ? "enabled — user will regain access" : "disabled — user will lose portal access"}.`} />
            )}
          </div>
        </div>

        {/* Card 2 — Region Access */}
        <div className={`bg-white rounded-xl border p-6 transition-colors ${editMode ? "border-yellow-200" : "border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={15} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Region Access</p>
          </div>
          <div className="space-y-2">
            {allRegions.map(region => (
              <label
                key={region.id}
                className={`flex items-center gap-3 p-2.5 rounded-lg ${editMode ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"}`}
              >
                <input
                  type="checkbox"
                  checked={form.region_ids.includes(region.id)}
                  onChange={() => toggleRegion(region.id)}
                  disabled={!editMode}
                  className="w-4 h-4 accent-emerald-600 disabled:opacity-60"
                />
                <span className={`text-sm ${form.region_ids.includes(region.id) ? "text-gray-800 font-medium" : "text-gray-400"}`}>
                  {region.name}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Card 3 — Feature Access */}
        <div className={`bg-white rounded-xl border p-6 transition-colors ${editMode ? "border-yellow-200" : "border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-5">
            <Sliders size={15} className="text-gray-400" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Feature Access</p>
          </div>
          <div className="space-y-4">
            {ALL_FEATURES.map(feature => (
              <div key={feature.key}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{feature.label}</span>
                  <button
                    onClick={() => toggleFeature(feature.key)}
                    disabled={!editMode}
                    className={`relative w-11 h-6 rounded-full transition-colors ${form.features[feature.key] ? "bg-emerald-600" : "bg-gray-300"} ${!editMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.features[feature.key] ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                </div>
                {editMode && form.features[feature.key] !== original.features[feature.key] && (
                  <FieldWarning message={`${feature.label} will be turned ${form.features[feature.key] ? "ON" : "OFF"}.`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Danger Zone — only in edit mode */}
      {editMode && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-4">Danger Zone</p>
          <div className="flex items-center gap-3">
            {user.status === "pending" && (
              <button onClick={handleResendInvite} disabled={resending}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Mail size={15} /> {resending ? "Sending..." : "Resend Invite"}
              </button>
            )}
            <button onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
              <Trash2 size={15} /> Delete User
            </button>
          </div>
        </div>
      )}
    </div>
  );
}